import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api.js';
import { focusHref, relativeDay } from './ScheduledList.jsx';

// T440 — the interviews, at the top, with the prep one click away.
//
// The star this whole product is steered by is interviews, and the moments
// with the most at stake had the least support in it. The evidence is not a
// theory: on 2026-09-29 the operator asked, by hand, for an interview prep
// document — while `interview_prep` had been in the product for months.
//
// KEYED ON STAGE, NOT ON A DATE. The roadmap had this band reading
// `nextEvent`, and measuring first killed that: of his seven applications
// at `interview`, SIX carry no recorded date at all. A band keyed on the
// date would have been empty six times out of seven — a feature assuming
// data he does not write. What he does have is the stage, so that is the
// key, and the missing date becomes the thing the band asks for rather than
// the reason it disappears.
//
// It links rather than mounting anything: `/applications?focus=<id>&open=…`
// (T415 + T440). A third mounting of the artefact modals is the direction
// T422 is trying to leave, and its adversarial review is why.

const STAGES = ['interview', 'screening'];

/**
 * T458 — what is coming before what already happened.
 *
 * The sort was one line, `nearest scheduled first`, over a timestamp that
 * can be in the past — so "fourteen days ago" counted as nearer than "in
 * three days". Measured on his own board the evening two interviews landed
 * on the same Thursday: both sat fifth and sixth, under four meetings that
 * had already taken place, and neither had prep. That inverts the band's
 * whole reason for existing — it was built because he asked by hand for a
 * prep document that the product had been able to write for months.
 *
 * `ScheduledList` puts overdue first and is right to: it is a to-do list,
 * and an overdue item there is an unresolved action. This band answers a
 * different question — what is coming — so the order is upcoming soonest
 * first, then what has happened most recently, then the undated.
 *
 * Undated stays last and is still not "long ago": six of his seven
 * interview rows carried no date when T440 measured, and sorting unknown to
 * the top would bury the one that is tomorrow.
 *
 * Exported pure, so the ordering is tested on rows rather than on a regex
 * over this file.
 */
export function sortInterviews(rows, now = new Date()) {
  const t = now.getTime();
  const key = (r) => {
    const at = r?.nextEvent?.at ? new Date(r.nextEvent.at).getTime() : NaN;
    if (!Number.isFinite(at)) return { bucket: 2, order: Infinity };
    return at >= t ? { bucket: 0, order: at } : { bucket: 1, order: -at };
  };
  return [...rows].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    return ka.bucket - kb.bucket || ka.order - kb.order;
  });
}

const InterviewsInPlay = forwardRef(function InterviewsInPlay({ onCount }, ref) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const results = await Promise.all(
        STAGES.map((s) => api.get(`/recommendations?status=${s}&limit=100`)),
      );
      const all = sortInterviews(results.flatMap((r) => r.recommendations ?? []));
      setRows(all);
      setFailed(false);
      onCount?.(all.length);
    } catch {
      setRows([]);
      setFailed(true);
      onCount?.(0);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);

  if (rows === null) return null;
  if (rows.length === 0) {
    // T387 — an empty section and a broken one are different sentences.
    return failed ? (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Interviews in play</h2>
        <p className="text-xs text-slate-500">Could not load these just now.</p>
      </section>
    ) : null;
  }

  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-medium text-brand-500">Interviews in play</h2>
        <span className="text-[11px] text-slate-500">{rows.length}</span>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li
            key={r.id}
            className="rounded-md border border-brand-700 bg-brand-600/5 px-3 py-2"
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-sm text-slate-100">{r.job?.company || 'unknown company'}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-400">{r.job?.title}</span>
              <span className="font-mono text-[11px] text-slate-500">{r.status}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
              {r.nextEvent?.at ? (
                <span className="text-brand-400">{relativeDay(r.nextEvent.at)}</span>
              ) : (
                // The band asks for the date instead of vanishing without it.
                <Link to={focusHref(r.id)} className="text-amber-400 underline decoration-dotted hover:text-amber-300">
                  no date set — add one
                </Link>
              )}
              <Link
                to={focusHref(r.id, 'interview-prep')}
                className="text-slate-300 underline decoration-dotted transition hover:text-brand-400"
              >
                {r.interviewPrep?.questions?.length ? 'Review prep' : 'Prepare me'}
              </Link>
              <Link
                to={focusHref(r.id)}
                className="text-slate-500 underline decoration-dotted transition hover:text-slate-300"
              >
                Open
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
});

export default InterviewsInPlay;
